import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { legacyDashboardURL } from '@helpers';
import {
  BodyWrapper,
  ActionObject,
  Header,
  Loader,
  MainWrapper,
} from '@components';
import { GlobalContext } from '@contexts';
import {
  businessQuery,
  siteDeleteMutation,
  siteQuery,
  siteMutation,
} from '@queries';
import { ChevronRightIcon } from '@svgs';

import { TemplateSidebar } from '../components/TemplateSidebar/TemplateSidebar.jsx';

import { TemplateSettingsForm } from './TemplateSettingsForm/TemplateSettingsForm.jsx';

export const TemplateSettings = () => {
  const { t, i18n } = useTranslation();
  const { businessID, siteID } = useParams();
  const globals = useContext(GlobalContext);
  // query client for triggering invalidations
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // get business
  const { data: businessData, isPending: businessIsPending } = useQuery(
    businessQuery(businessID),
  );

  // get site/template
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  // save template
  const mutation = useMutation(
    siteMutation(siteID, {
      onSuccess: (data) => {
        // invalidate the query so it is fetched again
        queryClient.invalidateQueries({
          queryKey: ['sites', 'detail', siteID],
        });
      },
    }),
  );

  // delete template
  const deleteMutation = useMutation(
    siteDeleteMutation(siteID, {
      onSuccess: () => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({ queryKey: ['sites', 'list'] });
        // navigate to the list view
        navigate(`/dashboard/projects/${businessID}/templates`);
      },
    }),
  );

  // download template in new tab
  const downloadTemplate = ()=> { 
    window.open(legacyDashboardURL(`/api/v2/sites/${siteID}/export_templates`), '_blank');
  }

  // shared isPending status
  const mutationPending = mutation.isPending || deleteMutation.isPending;

  return (
    <>
      <Header />
      <BodyWrapper>
        <TemplateSidebar
          businessID={businessID}
          businessData={businessData}
          siteID={siteID}
          siteData={siteData}
        />
        <MainWrapper>
          <div className="sb-main-breadcrumbs">
            <ul className="sb-flex">
              {globals.visible.projectsView && (
                <li className="sb-flex">
                  <Link to={`/dashboard/projects`}>Projects</Link>
                  <ChevronRightIcon />
                </li>
              )}
              <li className="sb-flex">
                {businessIsPending && <span>...</span>}
                {!businessIsPending && (
                  <Link to={`/dashboard/projects/${businessData.id}`}>
                    {businessData.business_name}
                  </Link>
                )}
                <ChevronRightIcon />
              </li>
              <li className="sb-flex">
                <Link to={`/dashboard/projects/${businessID}/templates`}>
                  Templates
                </Link>
                <ChevronRightIcon />
              </li>
              <li className="sb-flex">
                <Link
                  to={`/dashboard/projects/${businessID}/templates/${siteID}`}
                >
                  {siteIsPending && <>...</>}
                  {!siteIsPending && <>{siteData.title}</>}
                </Link>
                <ChevronRightIcon />
              </li>
              <li className="sb-flex">
                <span>{t('Template settings')}</span>
              </li>
            </ul>
          </div>
          <div className="sb-manage-content">
            <h1>{t('Template settings')}</h1>
            {siteIsPending && <Loader className="sb-loader sb-loader-large" />}
            {siteData && (
              <>
                <TemplateSettingsForm
                  siteData={siteData}
                  disabled={mutationPending}
                  mutation={mutation}
                />
                <div className="sb-message-action-section">
                  <ActionObject
                    onAction={() => deleteMutation.mutate()}
                    actionName="Disable"
                    actionVerb="Disabling"
                    label="Disable template"
                    note="This template will be disabled, but can be re-activated by searching"
                    loading={deleteMutation.isPending}
                  />
                  <ActionObject
                    onAction={downloadTemplate}
                    actionName="Download"
                    actionVerb="Downloading"
                    label="Download template (zip)"
                    note="This template will be downloaded as zip file"
                    color="success"
                  />
                </div>
              </>
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
